import {
  type AtticAssetUploadInput,
  AtticErrorCode,
  type AtticErrorFragment,
} from "@dashboard/graphql";

import { type AtticAssetCallOutcome, type AtticAssetHaltReason } from "./callOutcome";
import {
  type AtticAssetFileRefusal,
  getAtticAssetContentType,
  getUploadHeaders,
  isValidSubpath,
  validateAtticAssetFile,
} from "./utils";

export type AtticAssetUploadStage = "create" | "put" | "confirm";

interface MutationErrors {
  errors: AtticErrorFragment[];
}

export interface AtticAssetUploadCreateResult extends MutationErrors {
  asset: { id: string; key: string } | null;
  uploadUrl: string | null;
  headers: Array<{ name: string; value: string }> | null;
}

export interface AtticAssetPutRequest {
  url: string;
  /** Exactly the backend's list, nothing added. */
  headers: Record<string, string>;
  body: File;
  signal: AbortSignal;
  onProgress: (loaded: number, total: number) => void;
}

/**
 * The PUT to the bucket: resolves with the HTTP status the bucket answered,
 * rejects when no answer came (network, CORS, or aborted through `signal`).
 */
export type AtticAssetPut = (request: AtticAssetPutRequest) => Promise<{ status: number }>;

/**
 * The browser PUT. XMLHttpRequest rather than fetch, because only it reports
 * upload progress. Not a GraphQL call, so never Apollo; no credentials and no
 * header beyond the signed ones. The URL is used as given: nothing here reads
 * or assumes its host (it is the bucket's regional S3 endpoint today).
 *
 * A load with status 0 is no answer (a CORS refusal or a connection the
 * browser dropped looks like that), not an HTTP status to show.
 */
export const xhrPut: AtticAssetPut = ({ url, headers, body, signal, onProgress }) =>
  new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new Error("aborted"));

      return;
    }

    const xhr = new XMLHttpRequest();
    const onAbort = () => xhr.abort();
    const settle = (fn: () => void) => {
      signal.removeEventListener("abort", onAbort);
      xhr.upload.onprogress = null;
      xhr.onload = null;
      xhr.onerror = null;
      xhr.onabort = null;
      fn();
    };

    xhr.open("PUT", url);
    Object.entries(headers).forEach(([name, value]) => xhr.setRequestHeader(name, value));
    xhr.upload.onprogress = event =>
      onProgress(event.loaded, event.lengthComputable ? event.total : body.size);
    xhr.onload = () =>
      settle(() =>
        xhr.status === 0 ? reject(new Error("network")) : resolve({ status: xhr.status }),
      );
    xhr.onerror = () => settle(() => reject(new Error("network")));
    xhr.onabort = () => settle(() => reject(new Error("aborted")));
    signal.addEventListener("abort", onAbort);
    xhr.send(body);
  });

export interface AtticAssetUploadConfirmResult<TAsset> extends MutationErrors {
  asset: TAsset | null;
}

export interface AtticAssetUploadDeps<TAsset> {
  /**
   * atticAssetUploadCreate, read into an outcome (see callOutcome.ts). `signal`
   * aborts the request when it runs past `callTimeoutMs`.
   */
  createUpload: (
    input: AtticAssetUploadInput,
    signal: AbortSignal,
  ) => Promise<AtticAssetCallOutcome<AtticAssetUploadCreateResult>>;
  /** atticAssetUploadConfirm, read into an outcome (see callOutcome.ts); `signal` as for create. */
  confirmUpload: (
    key: string,
    signal: AbortSignal,
  ) => Promise<AtticAssetCallOutcome<AtticAssetUploadConfirmResult<TAsset>>>;
  /**
   * atticAssetDelete on this upload's own pending row (by the id its presign
   * returned): frees the key now rather than when the backend's reservation
   * lapses. Best effort; its outcome is not read. `signal` as for create.
   */
  releaseUpload: (assetId: string, signal: AbortSignal) => Promise<unknown>;
  put?: AtticAssetPut;
  /** Told as each stage starts, so the row can say what it is waiting on. */
  onStage?: (stage: AtticAssetUploadStage) => void;
  /** Share of the PUT sent, 0-1. */
  onProgress?: (fraction: number) => void;
  /** Cancels a queued create or a PUT (the reservation is released). Ignored once confirm starts. */
  signal?: AbortSignal;
  /** The PUT is abandoned after this long with no progress. */
  stallTimeoutMs?: number;
  /** Create, confirm and release are each given up on (as no answer) after this long. */
  callTimeoutMs?: number;
  /** The waits before each confirm retry; the number of retries is its length. */
  confirmRetryDelaysMs?: number[];
  wait?: (ms: number) => Promise<void>;
}

export interface AtticAssetUploadRequest {
  file: File;
  directoryId: string;
  subpath?: string;
  maxUploadBytes: number;
}

export type AtticAssetUploadFailureReason =
  /** The merchant (or leaving the page) stopped it. */
  | "cancelled"
  /** The PUT made no progress for `stallTimeoutMs`. */
  | "stalled"
  /**
   * Create got no answer: the backend may have reserved the name all the
   * same, and with no row id to release it by, it holds until its URL lapses.
   */
  | "createUnanswered"
  /**
   * The bytes landed but no confirm went through (no answer, or storage did
   * not show the object yet, every time): the file may or may not have gone live.
   */
  | "unconfirmed"
  /**
   * The first confirm was refused at the top level (a permission, a bad
   * argument), so the backend never ran it: the bytes stay under the pending
   * row until it expires. Not released: what a top-level error says about
   * the backend's work is not enough to remove a file by.
   */
  | "refusedAfterUpload"
  /**
   * A confirm got no answer and the retry was refused: the first one may have
   * gone through, so whether the file is live is not known.
   */
  | "outcomeUnknown"
  /** With stage "confirm", the bytes landed before the session or the store stopped it. */
  | AtticAssetHaltReason;

export type AtticAssetUploadFailure =
  | { ok: false; stage: "validate"; refusal: AtticAssetFileRefusal }
  | {
      ok: false;
      stage: AtticAssetUploadStage;
      /** The refusal's code, when the backend sent one. */
      code?: AtticErrorCode | null;
      /** The backend's sentence, when it sent one. */
      message: string | null;
      /** The bucket's HTTP status, for a refused PUT. */
      status?: number;
      reason?: AtticAssetUploadFailureReason;
    };

/**
 * `asset` is what the CONFIRM answered (never the presign's pending row), or
 * null when confirm said the key was already live (a lost answer, retried).
 */
export type AtticAssetUploadResult<TAsset> =
  | { ok: true; asset: TAsset | null }
  | AtticAssetUploadFailure;

export const STALL_TIMEOUT_MS = 60_000;

export const CALL_TIMEOUT_MS = 30_000;

/** About half a minute in all, so a slow bucket or a busy API gets time to catch up. */
export const CONFIRM_RETRY_DELAYS_MS = [1_000, 2_000, 4_000, 8_000, 15_000];

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

/** The refusal a row reports: the first error, with its code and sentence. */
const refusalOf = (errors: AtticErrorFragment[] | undefined) => {
  const error = errors?.find(item => !!item.message) ?? errors?.[0];

  return { code: error?.code ?? null, message: error?.message || null };
};

/**
 * One API call, bounded: past `timeoutMs` its request is aborted (Apollo's
 * upload link hands `context.fetchOptions.signal` to fetch) and the call reads
 * as no answer, even if the abort itself never lands. A throw is no answer too.
 */
const askWithin = async <TPayload>(
  call: (signal: AbortSignal) => Promise<AtticAssetCallOutcome<TPayload>>,
  timeoutMs: number,
): Promise<AtticAssetCallOutcome<TPayload>> => {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timedOut = new Promise<AtticAssetCallOutcome<TPayload>>(resolve => {
    timer = setTimeout(() => {
      controller.abort();
      resolve({ kind: "noAnswer" });
    }, timeoutMs);
  });

  try {
    return await Promise.race([call(controller.signal), timedOut]);
  } catch (error) {
    return { kind: "noAnswer" };
  } finally {
    clearTimeout(timer);
  }
};

/** Why the browser will not even try this upload, or null when it may go. */
export const validateAtticAssetUpload = ({
  file,
  subpath,
  maxUploadBytes,
}: Pick<
  AtticAssetUploadRequest,
  "file" | "subpath" | "maxUploadBytes"
>): AtticAssetFileRefusal | null =>
  validateAtticAssetFile(file, maxUploadBytes) ??
  (subpath && !isValidSubpath(subpath) ? { reason: "folder" } : null);

/**
 * One file into the store's bucket: reserve and sign (atticAssetUploadCreate),
 * PUT the bytes straight to S3 with every signed header verbatim, then
 * atticAssetUploadConfirm. Nothing is confirmed unless the PUT came back 2xx.
 *
 * The signed URL is short-lived and single use (If-None-Match: *), so it is
 * asked for immediately before its PUT and a PUT is never retried. Reservation
 * (attic/assets/service.py `presign_upload`): the backend holds a key for as long
 * as its signed URL can still be used, then lets a new presign take it over
 * when no object landed; bytes that landed hold it until they are confirmed or
 * the pending upload expires.
 *
 * Release (atticAssetDelete on this upload's own pending row) follows a PUT that
 * was cancelled, stalled, got no answer, or was refused with any status but
 * 412. It acts through this upload's row id only: a key has one row, so it can
 * never remove a file that went live under another upload, and whatever it
 * removes at the key arrived through this reservation.
 *
 * A 412 (If-None-Match: * found an object at the key) goes on to confirm.
 * Presign refused the key if anything was in storage, and no other presign
 * can have it while this URL is alive (`_release`), so the object is this
 * upload's own bytes: a PUT the network sent twice, the first of which
 * landed. Confirm checks the object as for any PUT and says what became of it.
 *
 * Create, confirm and release each give up after `callTimeoutMs`. A confirm
 * that gets no answer, or ASSET_UPLOAD_NOT_LANDED (storage does not show the
 * object yet), is retried with a bounded backoff. A retry answered
 * ASSET_ALREADY_CONFIRMED means an earlier one landed; any other refusal of a
 * retry after a lost answer leaves the outcome unknown, and the row says so.
 */
export async function uploadAtticAsset<TAsset>(
  { file, directoryId, subpath, maxUploadBytes }: AtticAssetUploadRequest,
  {
    createUpload,
    confirmUpload,
    releaseUpload,
    put = xhrPut,
    onStage,
    onProgress,
    signal,
    stallTimeoutMs = STALL_TIMEOUT_MS,
    callTimeoutMs = CALL_TIMEOUT_MS,
    confirmRetryDelaysMs = CONFIRM_RETRY_DELAYS_MS,
    wait = sleep,
  }: AtticAssetUploadDeps<TAsset>,
): Promise<AtticAssetUploadResult<TAsset>> {
  const refusal = validateAtticAssetUpload({ file, subpath, maxUploadBytes });

  if (refusal) {
    return { ok: false, stage: "validate", refusal };
  }

  const cancelled = (stage: AtticAssetUploadStage): AtticAssetUploadFailure => ({
    ok: false,
    stage,
    message: null,
    reason: "cancelled",
  });

  if (signal?.aborted) {
    return cancelled("create");
  }

  onStage?.("create");

  // Not aborted by a cancel: the reservation it may make is only known (and
  // so only released) once it answers.
  const created = await askWithin(
    callSignal =>
      createUpload(
        {
          directory: directoryId,
          filename: file.name,
          // validateAtticAssetFile has just said this is not null.
          contentType: getAtticAssetContentType(file) as string,
          size: file.size,
          ...(subpath ? { subpath } : {}),
        },
        callSignal,
      ),
    callTimeoutMs,
  );

  const release = (assetId: string) =>
    // Best effort: when it does not reach the backend, the reservation lapses there.
    askWithin(async callSignal => {
      await releaseUpload(assetId, callSignal);

      return { kind: "noAnswer" };
    }, callTimeoutMs);

  if (created.kind === "halted") {
    return { ok: false, stage: "create", message: null, reason: created.reason };
  }

  if (created.kind !== "answered") {
    // Whatever went wrong, a merchant who cancelled meanwhile sees "Cancelled".
    if (signal?.aborted) {
      return cancelled("create");
    }

    return created.kind === "refused"
      ? { ok: false, stage: "create", message: created.message }
      : { ok: false, stage: "create", message: null, reason: "createUnanswered" };
  }

  const payload = created.payload;

  if (payload.errors.length || !payload.asset || !payload.uploadUrl) {
    if (signal?.aborted && payload.asset) {
      await release(payload.asset.id);
    }

    return signal?.aborted
      ? cancelled("create")
      : { ok: false, stage: "create", ...refusalOf(payload.errors) };
  }

  const { asset: reserved, uploadUrl } = payload;

  if (signal?.aborted) {
    await release(reserved.id);

    return cancelled("create");
  }

  onStage?.("put");

  const putController = new AbortController();
  const onOuterAbort = () => putController.abort();
  let stalled = false;
  let stallTimer: ReturnType<typeof setTimeout> | undefined;
  const armStallTimer = () => {
    clearTimeout(stallTimer);
    stallTimer = setTimeout(() => {
      stalled = true;
      putController.abort();
    }, stallTimeoutMs);
  };

  signal?.addEventListener("abort", onOuterAbort);
  armStallTimer();

  let putFailure: AtticAssetUploadFailure | null = null;

  try {
    const response = await put({
      url: uploadUrl,
      // The signed list differs per content type (an SVG is signed with
      // Content-Disposition: attachment, attic/assets/storage.py
      // `presigned_put_headers()`); it goes out exactly as it came.
      headers: getUploadHeaders(payload.headers),
      body: file,
      signal: putController.signal,
      onProgress: (loaded, total) => {
        armStallTimer();
        onProgress?.(total > 0 ? Math.min(1, loaded / total) : 0);
      },
    });

    // A 412 is this upload's own bytes already at the key (see above): confirm.
    if (response.status !== 412 && (response.status < 200 || response.status >= 300)) {
      putFailure = { ok: false, stage: "put", message: null, status: response.status };
    }
  } catch (error) {
    putFailure = signal?.aborted
      ? cancelled("put")
      : stalled
        ? { ok: false, stage: "put", message: null, reason: "stalled" }
        : { ok: false, stage: "put", message: null };
  } finally {
    clearTimeout(stallTimer);
    signal?.removeEventListener("abort", onOuterAbort);
  }

  if (putFailure) {
    await release(reserved.id);

    return putFailure;
  }

  // From here a cancel is ignored: the bytes are in the bucket, and only
  // confirm can say what became of them. Nothing below uses the presign's
  // row id: what confirm answers is the file.
  onStage?.("confirm");

  const { key } = reserved;
  let answerLost = false;

  for (let attempt = 0; ; attempt++) {
    const confirmed = await askWithin(callSignal => confirmUpload(key, callSignal), callTimeoutMs);

    if (confirmed.kind === "halted") {
      return { ok: false, stage: "confirm", message: null, reason: confirmed.reason };
    }

    if (confirmed.kind === "answered") {
      const { errors, asset } = confirmed.payload;

      if (!errors.length && asset) {
        return { ok: true, asset };
      }

      // The key this upload reserved is live: an earlier confirm whose
      // answer was lost made it so.
      if (errors.some(error => error.code === AtticErrorCode.ASSET_ALREADY_CONFIRMED)) {
        return { ok: true, asset: null };
      }

      // Storage does not show the object yet: ask again, like a lost answer.
      if (!errors.some(error => error.code === AtticErrorCode.ASSET_UPLOAD_NOT_LANDED)) {
        // A first confirm refused: the backend has deleted the object and the
        // row. A retry refused after a lost answer: the lost one may have gone
        // through, so the row cannot say which.
        return {
          ok: false,
          stage: "confirm",
          ...refusalOf(errors),
          ...(answerLost ? { reason: "outcomeUnknown" as const } : {}),
        };
      }
    }

    if (confirmed.kind === "refused") {
      return {
        ok: false,
        stage: "confirm",
        message: confirmed.message,
        reason: answerLost ? "outcomeUnknown" : "refusedAfterUpload",
      };
    }

    if (confirmed.kind === "noAnswer") {
      answerLost = true;
    }

    // No answer, or not landed yet: ask again, a bounded number of times.
    if (attempt >= confirmRetryDelaysMs.length) {
      return { ok: false, stage: "confirm", message: null, reason: "unconfirmed" };
    }

    await wait(confirmRetryDelaysMs[attempt]);
  }
}
